import { inject } from '@angular/core';
import { CanActivateChildFn, Params, Router, UrlTree } from '@angular/router';
import { ATELIER_FILTER_CONFIG } from '../config/atelier-filter.config';
import { MPR_FILTER_CONFIG } from '../config/mpr-filter.config';
import { VN_FILTER_CONFIG } from '../config/vn-filter.config';
import { VO_FILTER_CONFIG } from '../config/vo-filter.config';
import { CockpitFilterStateService } from '../services/cockpit-filter-state.service';
import { mapCockpitFilters } from '../utils/cockpit-filter.utils';

const GPS_FILTER_KEYS = ['id_plaque', 'id_affaire', 'id_affaire_only', 'id_point_relais', 'id_agent'];

const FILTER_CONFIGS = [...ATELIER_FILTER_CONFIG, ...MPR_FILTER_CONFIG, ...VN_FILTER_CONFIG, ...VO_FILTER_CONFIG];

// Paramètres conservés à destination en dehors des filtres par page.
const GLOBAL_QUERY_KEYS = [...GPS_FILTER_KEYS, 'current_page', 'nbr_items_per_page', 'order_by', 'direction', 'search'];

/**
 * Guard qui maintient les filtres GPS (plaque, affaire, point de relais, agent) persistants
 * lors de la navigation entre les différentes pages de GPS.
 *
 * - Si l'URL n'a pas de paramètre GPS mais que le service a un état sauvegardé,
 *   redirige vers la même URL en ajoutant les paramètres GPS sauvegardés.
 * - Si l'URL a des paramètres GPS, les synchronise dans le service.
 * - Si l'utilisateur réinitialise le filtre (via CockpitFilterComponent.resetFilter()),
 *   le service est vidé et le guard laisse passer sans redirection.
 */
export const cockpitFilterGuard: CanActivateChildFn = (childRoute, state) => {
  const cockpitFilterState = inject(CockpitFilterStateService);
  const router = inject(Router);

  const source = router.routerState.snapshot;
  const sourcePath = getPath(source.url);
  const targetPath = getPath(state.url);
  const trigger = router.currentNavigation()?.trigger;

  const samePage = sourcePath === targetPath;
  const fromHistory = trigger === 'popstate' || trigger === 'hashchange';

  let nextParams: Params = { ...childRoute.queryParams };

  // Une modification sur place ou un retour dans l'historique
  // doit conserver les paramètres demandés.
  if (!samePage && !fromHistory) {
    // Tes chemins de configuration sont relatifs à /cockpit.
    const pageConfig = FILTER_CONFIGS.find(({ path }) => getPath(`/cockpit/${path}`) === targetPath);

    // Transférer et nettoyer lors d'un changement de page Cockpit.
    if (pageConfig && sourcePath.startsWith('/cockpit/')) {
      nextParams = {
        ...mapCockpitFilters(pageConfig, source.root.queryParams, nextParams),
        ...pickParams(nextParams, GLOBAL_QUERY_KEYS),
      };
    }

    // Conserver la persistance GPS existante.
    // Une clé présente, même vide, empêche sa restauration.
    const hasGpsParam = GPS_FILTER_KEYS.some((key) => Object.prototype.hasOwnProperty.call(nextParams, key));

    if (!hasGpsParam) {
      nextParams = {
        ...nextParams,
        ...cockpitFilterState.getState(),
      };
    }
  }

  const targetTree = router.parseUrl(state.url);
  const nextTree = new UrlTree(targetTree.root, nextParams, targetTree.fragment);

  // Rediriger uniquement si les paramètres ont changé.
  if (router.serializeUrl(nextTree) !== router.serializeUrl(targetTree)) {
    return nextTree;
  }

  // Synchroniser l'état une fois l'URL stabilisée.
  cockpitFilterState.setState(cockpitFilterState.extractFromQueryParams(nextParams));

  return true;
};

function getPath(url: string): string {
  return url.split(/[?#]/)[0].replace(/\/+$/, '');
}

function pickParams(params: Params, keys: string[]): Params {
  return Object.fromEntries(Object.entries(params).filter(([key]) => keys.includes(key)));
}
